/**
 * BillDatePanel — Settings → Accounting export → "Bill on" (hauler billing gap 6, 0250).
 *
 * GET / PATCH /v1/tenants/bill-date-settings. The day each ticket is billed on, its service date:
 * the day the job finished (the default, and how billing always worked) or the day it started.
 * The API prices the disposal fee on that day and prints it as the Date on invoice lines, the
 * PDF, the ticket pages and the QuickBooks export. Changing it never re-dates a ticket already
 * approved. Only MANAGE_TENANT holders (`canEdit`) can change it, the API's own gate.
 *
 * The values are local, not imported from @lodex-pro/shared: the shared barrel pulls drizzle
 * into a web bundle (as completion-policy.ts notes).
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';

export type BillDateBasis = 'FINISHED' | 'STARTED';

export const BILL_DATE_QUERY_KEY = ['tenants', 'bill-date-settings'] as const;

export const BILL_DATE_OPTIONS: ReadonlyArray<{
  readonly value: BillDateBasis;
  readonly label: string;
  readonly help: string;
}> = [
  {
    value: 'FINISHED',
    label: 'The day the job finished',
    help: 'Each ticket is dated by the day the driver completed it.',
  },
  {
    value: 'STARTED',
    label: 'The day the job started',
    help: "Each ticket is dated by the day the work began: the driver's arrival, or the start of work, as the customer's billing clock is set.",
  },
];

/** The one plain sentence under the heading. */
export const BILL_DATE_EXPLANATION =
  'The date on each invoice line, the PDF, the ticket pages and the QuickBooks export, and the day whose disposal fee applies. Changing it only affects tickets approved after the change.';

export interface BillDatePanelProps {
  readonly canEdit: boolean;
}

export function BillDatePanel({ canEdit }: BillDatePanelProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useApiQuery<{ readonly billDateBasis: BillDateBasis }>(
    apiOpts,
    '/v1/tenants/bill-date-settings',
    { queryKey: BILL_DATE_QUERY_KEY },
  );
  const mutation = useApiMutation<{ billDateBasis: BillDateBasis }, { billDateBasis: string }>(
    apiOpts,
    '/v1/tenants/bill-date-settings',
    'PATCH',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: BILL_DATE_QUERY_KEY });
        toast.success('Saved');
      },
      onError: () => {
        toast.error('Could not save the setting. Try again.');
      },
    },
  );

  return (
    <div style={{ maxWidth: '640px' }} data-testid="bill-date-panel">
      <h2
        style={{
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 4px',
        }}
      >
        Bill on
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        {BILL_DATE_EXPLANATION}
      </p>
      {isError ? (
        <p style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
          Failed to load this setting.
        </p>
      ) : isLoading || data === undefined ? (
        <p style={{ color: 'var(--color-text-tertiary)', fontSize: '14px' }}>Loading…</p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Bill on"
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          {BILL_DATE_OPTIONS.map((o) => (
            <label
              key={o.value}
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                cursor: canEdit ? 'pointer' : 'default',
              }}
            >
              <input
                type="radio"
                name="bill-date-basis"
                value={o.value}
                checked={data.billDateBasis === o.value}
                disabled={!canEdit || mutation.isPending}
                onChange={(): void => mutation.mutate({ billDateBasis: o.value })}
              />
              <span>
                <span
                  style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  {o.label}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {o.help}
                </span>
              </span>
            </label>
          ))}
          {!canEdit ? (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Only a company admin can change this.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
